<!-- Instrument details (side panel): price with purchase dates, purchase returns, lots. -->
<script lang="ts">
  import * as E from "../core/engine";
  import { app, need } from "../lib/state.svelte";
  import { esc, fmtDate, fmtNum, fmtQtyTrim, msDay, pctPlain } from "../lib/format";
  import { baseOption, daySeries, lineSeries, palette, series, timeAxis, tipMoney, valueAxis, type TipParam } from "../lib/chart";
  import Kpi from "../components/Kpi.svelte";
  import Card from "../components/Card.svelte";
  import Chart from "../components/Chart.svelte";
  import Seg from "../components/Seg.svelte";
  import Money from "../components/Money.svelte";
  import Signed from "../components/Signed.svelte";
  import Pct from "../components/Pct.svelte";
  import PurchaseTable from "./parts/PurchaseTable.svelte";
  import LotsTable from "./parts/LotsTable.svelte";
  import RealizedTable from "./parts/RealizedTable.svelte";

  let { key }: { key: string } = $props();
  const m = $derived(need(app.model));
  const S = $derived(new Set(app.accList));
  const syms = $derived(Object.values(m.instr).filter((x) => x.key === key).map((x) => x.symbol));
  const info = $derived(m.instr[syms[0]]);
  const e = $derived(m.idx(m.today));
  // main listing: the one with the largest current value, otherwise the first with market quotes
  const mainDefault = $derived.by(() => {
    let main = syms[0], best = -1;
    for (const s of syms) {
      let v = 0;
      for (const a of S) v += (m.acc[a]?.qty[s]?.[e] || 0) * (m.pPLN(s, e) || 0);
      if (v > best && m.priceSource[s] !== "none") { best = v; main = s; }
    }
    return main;
  });
  let chosen = $state<string | null>(null);
  const sym = $derived(chosen && syms.includes(chosen) ? chosen : mainDefault);
  const purchases = $derived(E.purchaseAnalysis(m, [...S]).filter((p) => p.key === key));
  const lots = $derived(E.openLots(m, [...S]).filter((l) => l.key === key));
  const closed = $derived(m.realized.filter((x) => x.key === key && S.has(x.account)));
  const hold = $derived(E.holdings(m, [...S], m.today, m.today, { includeClosed: true }).find((h) => h.key === key));
  const first = $derived(purchases.length ? purchases.reduce((a, p) => (p.day < a ? p.day : a), purchases[0].day) : m.firstDay);
  const mode = $derived(app.ui.instrMode || "pln");
  const realizedSum = $derived(closed.reduce((s, x) => s + x.gainPLN, 0));

  const priceOpt = $derived.by(() => {
    const md = mode;
    const p = palette();
    const s0 = Math.max(0, m.idx(first) - 20);
    const ccy = m.priceCcy[sym];
    const priceAt = (i: number) => (md === "pln" ? m.pPLN(sym, i) : m.priceArr[sym][i]);
    const line = daySeries(m, m.priceArr[sym], s0, e, (_v, i) => { const x = priceAt(i); return isNaN(x) ? null : +x.toFixed(4); });
    const maxCost = Math.max(1, ...purchases.map((x) => x.costPLN));
    const buyPts = purchases.map((x) => {
      const i = m.idx(x.day);
      const y = md === "pln" ? x.unitPLN : x.priceCcy === ccy && x.symbol === sym ? x.price : priceAt(i);
      return { value: [x.day, y], symbolSize: 8 + 16 * Math.sqrt(x.costPLN / maxCost), itemStyle: { color: (x.retNow ?? 0) >= 0 ? p.gain : p.loss, borderColor: p.surface, borderWidth: 2 }, x };
    });
    const sells = m.txs.filter((t) => t.type === "SELL" && syms.includes(t.symbol) && S.has(t.account)).map((t) => {
      const i = m.idx(t.day);
      const y = md === "pln" ? (t.amt * m.fx(t.currency, i)) / t.qty : t.priceCcy === ccy && t.symbol === sym ? t.price : priceAt(i);
      return { value: [t.day, y], t };
    });
    // average cost of open lots over time (PLN)
    const avg: [string, number | null][] = [];
    if (md === "pln") {
      for (let i = s0; i <= e; i++) {
        let q = 0, c = 0;
        for (const a of S) for (const s of syms) { q += m.acc[a]?.qty[s]?.[i] || 0; c += m.acc[a]?.symCost?.[s]?.[i] || 0; }
        avg.push([m.days[i], q > 1e-9 ? +(c / q).toFixed(4) : null]);
      }
    }
    const unit = md === "pln" ? "zł" : ccy;
    const one = (x: TipParam) => {
      if (x.seriesName === "Zakupy") { const q = x.data.x; return `<b>Zakup ${fmtDate(q.day)}</b><br>${esc(q.account)} · ${esc(q.symbol)}<br>${fmtQtyTrim(q.qty)} szt. × ${fmtNum(q.price, 3)} ${q.priceCcy}<br>koszt ${tipMoney(q.costPLN, 2)} (${fmtNum(q.unitPLN, 2)} zł/szt.)<br>dziś: <b>${pctPlain(q.retNow)}</b>${q.annNow !== null ? ` (${pctPlain(q.annNow)}/rok)` : ""}`; }
      if (x.seriesName === "Sprzedaże") { const t = x.data.t; return `<b>Sprzedaż ${fmtDate(t.day)}</b><br>${fmtQtyTrim(t.qty)} szt. × ${fmtNum(t.price, 3)} ${t.priceCcy}`; }
      return "";
    };
    return {
      ...baseOption(p),
      tooltip: { ...baseOption(p).tooltip, trigger: "axis", formatter: (ps: TipParam[]) => {
        const day = msDay(ps[0].axisValue);
        const lineP = ps.filter((x) => x.seriesType === "line" && x.value && x.value[1] !== null);
        const pts = ps.filter((x) => x.seriesType === "scatter" && x.value && x.value[0] === day);
        return `<div style="font-weight:600">${fmtDate(day)}</div>` + lineP.map((x) => `${x.marker}${esc(x.seriesName)}: <b>${fmtNum(x.value[1], 2)} ${unit}</b>`).join("<br>") + pts.map((x) => "<hr style='border:0;border-top:1px solid " + p.grid + "'>" + one(x)).join("");
      } },
      xAxis: timeAxis(p), yAxis: valueAxis((v) => fmtNum(v, v < 10 ? 2 : 0), p, { scale: true }),
      series: [
        lineSeries(`Kurs ${sym} (${unit})`, line, series(0), { triggerLineEvent: true }),
        ...(md === "pln" ? [lineSeries("Średni koszt otwartych partii", avg, p.muted, { step: "end", lineStyle: { width: 1.5, color: p.muted } })] : []),
        { name: "Zakupy", type: "scatter", data: buyPts, z: 5, itemStyle: { color: p.gain } },
        { name: "Sprzedaże", type: "scatter", symbol: "triangle", symbolRotate: 180, symbolSize: 12, data: sells, itemStyle: { color: p.ink }, z: 6 },
      ],
    };
  });
</script>

<div class="view-head"><h1>{info.name}</h1><span class="sub">{syms.join(", ")}{info.isin ? " · ISIN " + info.isin : ""} · {info.group}</span></div>
<div class="kpis">
  <Kpi label="Wartość" sub="{fmtNum(hold?.qty || 0, 4).replace(/,?0+$/, '')} szt.">{#snippet value()}<Money v={hold?.value || 0} />{/snippet}</Kpi>
  <Kpi label="Koszt nabycia (otwarte)">
    {#snippet value()}<Money v={hold?.cost || 0} />{/snippet}
    {#snippet sub()}{#if hold?.avgCost}<Money v={hold.avgCost} d={2} /> / szt.{/if}{/snippet}
  </Kpi>
  <Kpi label="Zysk niezrealizowany">{#snippet value()}<Signed v={hold?.unrealized || 0} />{/snippet}{#snippet sub()}<Pct v={hold?.unrealizedPct} />{/snippet}</Kpi>
  <Kpi label="Zysk zrealizowany" sub="{closed.length} zamkniętych partii">{#snippet value()}<Signed v={realizedSum} />{/snippet}</Kpi>
  <Kpi label="Zakupów" value={String(purchases.length)} sub={purchases.length ? `od ${fmtDate(first)}` : ""} />
</div>
<Card title="Kurs i daty zakupów" hint="punkty: zakupy (zielone — dziś na plusie, czerwone — na minusie; wielkość ∝ kwota), trójkąty: sprzedaże">
  {#snippet extra()}
    <div class="row">
      <select id="mainSym" value={sym} onchange={(ev) => (chosen = ev.currentTarget.value)}>{#each syms as s (s)}<option>{s}</option>{/each}</select>
      <Seg id="modeSeg" attr="m" options={[["pln", "PLN"], ["ccy", "waluta notowania"]] as const} value={mode} onselect={(k) => (app.ui.instrMode = k)} />
    </div>
  {/snippet}
  <Chart id="chPrice" class="tall" option={priceOpt} />
</Card>
<Card title="Opłacalność każdego zakupu w kolejnych horyzontach" hint="zmiana wartości zakupu (PLN, z prowizją) po danym czasie od zakupu"><PurchaseTable list={purchases} withName={false} /></Card>
<Card title="Otwarte partie (FIFO)"><LotsTable {lots} /></Card>
{#if closed.length}<Card title="Sprzedane partie"><RealizedTable list={closed} /></Card>{/if}
